import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/src/shared/lib/shadcn';
import { FOCUS_RING } from './control-styles';

// A centered box of the touch-target size (size-11) the pointer can hit on touch
// screens, whatever the visible size of an icon button; a fine pointer gets nothing.
const COARSE_HIT_AREA =
  "relative pointer-coarse:before:absolute pointer-coarse:before:top-1/2 pointer-coarse:before:left-1/2 pointer-coarse:before:size-11 pointer-coarse:before:-translate-1/2 pointer-coarse:before:content-['']";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // The ring sits off the fill, so it reads against the card and not against the fill.
        default: `bg-primary-action text-primary-action-foreground hover:bg-primary-action/90 ${FOCUS_RING}`,
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border border-secondary-action-border bg-secondary-action text-secondary-action-foreground shadow-xs hover:bg-secondary-action-hover dark:shadow-none',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-foreground underline underline-offset-4 dark:text-primary',
        /** The secondary action of a view, next to a primary `action`. */
        subtle: 'bg-(--sc-subtle) text-foreground hover:bg-(--sc-subtle)/70',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: `size-9 ${COARSE_HIT_AREA}`,
        'icon-sm': `size-8 ${COARSE_HIT_AREA}`,
        'icon-lg': `size-10 ${COARSE_HIT_AREA}`,
        /** A view's primary or secondary action: a touch target on phones, a regular control from sm. */
        action: `h-auto min-h-11 sm:min-h-9 py-1.5 rounded-xl px-[18px] text-sm font-semibold [&_svg:not([class*='size-'])]:size-[18px] ${FOCUS_RING}`,
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
